import { NoCodeAppType } from '@wpp-open/core'
import { useLocation } from 'react-router-dom'

import { PageBuilderRender } from 'components/apps/common/pageBuilderRender/PageBuilderRender'
import { PenpalParentFrame } from 'components/apps/common/penpalParentFrame/PenpalParentFrame'
import styles from 'components/apps/fullscreen/noCodeApp/defaultNoCodeAppRenderer/FullscreenDefaultNoCodeAppRenderer.module.scss'
import { NoCodeAppMiroBoard, RenderableNoCodeApp } from 'types/apps/noCodeApps'
import { getAppInstanceUrl } from 'utils/navigation'

interface Props {
  app: Exclude<RenderableNoCodeApp, NoCodeAppMiroBoard>
}

export const FullscreenDefaultNoCodeAppRenderer = ({ app }: Props) => {
  const { stableId, type, osRoute } = app
  const location = useLocation()
  const basePath = getAppInstanceUrl({ id: stableId, osRoute })

  // Combine pathname and query string from the current URL
  const fullLocation = location.pathname + location.search

  // Remove the basePath to get only the dynamic part of the route
  // Example: "/apps/123?foo=bar" becomes "?foo=bar"
  const relativeSubPath = fullLocation.replace(basePath, '')

  // Check if the subPath starts with "?" (meaning it's only a query string)
  // If so, encode it completely to prevent it from being parsed by the penpal parent frame host
  const subPath = relativeSubPath.startsWith('?')
    ? encodeURIComponent(relativeSubPath) // Encodes "?foo=bar" as "%3Ffoo%3Dbar"
    : relativeSubPath // Otherwise, leave the path or path+query as-is

  return (
    <>
      {type === NoCodeAppType.EmbeddedLink && (
        <PenpalParentFrame
          key={stableId}
          isFullscreen
          appId={stableId}
          allow="clipboard-write"
          sandbox="allow-downloads allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
          className={styles.iframe}
          src={subPath ? `${app.url}${subPath}` : app.url}
        />
      )}

      {type === NoCodeAppType.EmbeddedCode && (
        <PenpalParentFrame
          key={stableId}
          isFullscreen
          appId={stableId}
          allow="clipboard-write"
          sandbox="allow-downloads allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
          className={styles.iframe}
          srcEmbeddedCode={app.embeddedCode}
        />
      )}

      {type === NoCodeAppType.CodeUpload && (
        <iframe
          key={stableId}
          allow="clipboard-write"
          sandbox="allow-downloads allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
          className={styles.iframe}
          src={subPath ? `${app.url}${subPath}` : app.url}
        />
      )}

      {type === NoCodeAppType.PageBuilder && <PageBuilderRender key={stableId} app={app} />}
    </>
  )
}
